import type { FC, HTMLAttributes, MouseEventHandler, ReactNode } from "react";

import { ChevronDown, Share04 } from "@untitledui/icons";
import { Link as AriaLink } from "react-aria-components";

import { Badge } from "@/components/ui/badges/badges";
import { cx, sortCx } from "@/utils/cx";

const styles = sortCx({
	root: "group relative flex w-full cursor-pointer items-center rounded-md text-white/80 outline-focus-ring transition duration-100 ease-linear select-none hover:bg-white/10 focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
	rootSelected: "bg-white/10 text-white",
});

interface NavItemBaseProps {
	/** Badge to display. */
	badge?: ReactNode;
	/** Content to display. */
	children?: ReactNode;
	/** Whether the nav item is currently active. */
	current?: boolean;
	/** URL to navigate to when the nav item is clicked. */
	href?: string;
	/** Icon component to display. */
	icon?: FC<HTMLAttributes<HTMLOrSVGElement>>;
	/** Whether the nav item shows only an icon. */
	iconOnly?: boolean;
	/** Handler for click events. */
	onClick?: MouseEventHandler;
	/** Whether the collapsible nav item is open. */
	open?: boolean;
	/** Test ID for targeting in tests. */
	testId?: string;
	/** Whether to truncate the label text. */
	truncate?: boolean;
	/** Type of the nav item. */
	type: "collapsible" | "collapsible-child" | "link";
}

export const NavItemBase = ({
	badge,
	children,
	current,
	href,
	icon: Icon,
	onClick,
	testId,
	truncate = true,
	type,
}: NavItemBaseProps) => {
	const iconElement = Icon && (
		<Icon
			aria-hidden="true"
			className="mr-2 size-5 shrink-0 transition-inherit-all"
		/>
	);

	const badgeElement =
		badge && (typeof badge === "string" || typeof badge === "number") ? (
			<Badge className="ml-3" color="gray" size="sm" type="pill-color">
				{badge}
			</Badge>
		) : (
			badge
		);

	const labelElement = (
		<span
			className={cx(
				"flex-1 text-md font-semibold transition-inherit-all group-hover:text-white/100",
				truncate && "truncate",
				current && "text-white",
			)}
		>
			{children}
		</span>
	);

	const isExternal = href && href.startsWith("http");
	const externalIcon = isExternal && (
		<Share04 className="size-4 stroke-[2.5px] text-fg-quaternary" />
	);

	if (type === "collapsible") {
		return (
			<summary
				className={cx("px-3 py-2", styles.root, current && styles.rootSelected)}
				data-testid={testId}
				onClick={onClick}
			>
				{iconElement}

				{labelElement}

				{badgeElement}

				<ChevronDown
					aria-hidden="true"
					className="ml-3 size-4 shrink-0 stroke-[2.5px] text-white/80 in-open:-scale-y-100"
				/>
			</summary>
		);
	}

	if (type === "collapsible-child") {
		return (
			<AriaLink
				aria-current={current ? "page" : undefined}
				className={cx(
					"py-2 pr-3 pl-10",
					styles.root,
					current && styles.rootSelected,
				)}
				data-testid={testId}
				href={href!}
				onClick={onClick}
				rel="noopener noreferrer"
				target={isExternal ? "_blank" : "_self"}
			>
				{labelElement}
				{externalIcon}
				{badgeElement}
			</AriaLink>
		);
	}

	return (
		<AriaLink
			aria-current={current ? "page" : undefined}
			className={cx("px-3 py-2", styles.root, current && styles.rootSelected)}
			data-testid={testId}
			href={href!}
			onClick={onClick}
			rel="noopener noreferrer"
			target={isExternal ? "_blank" : "_self"}
		>
			{iconElement}
			{labelElement}
			{externalIcon}
			{badgeElement}
		</AriaLink>
	);
};
